import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginatedResponse, SearchParams } from '../types';
import { rateLimitErrorMessage } from '../utils/errorMessage';
import { useDebouncedValue } from './useDebouncedValue';
import { useLanguage } from '../contexts/LanguageContext';

export interface PaginatedQueryState<T> {
  data: PaginatedResponse<T> | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

type PageFetcher<T, P> = (params: P) => Promise<PaginatedResponse<T>>;

/** Shared server-state lifecycle for paginated, searchable resources. */
export const usePaginatedQuery = <T, P extends SearchParams = SearchParams>(
  params: P,
  fetchPage: PageFetcher<T, P>,
  errorFallback: string,
  searchField: keyof P = 'name' as keyof P,
  enabled = true,
  resourceKey = 'paginated',
): PaginatedQueryState<T> => {
  const { t } = useLanguage();
  const searchValue = params[searchField];
  const debouncedSearchValue = useDebouncedValue(
    typeof searchValue === 'string' ? searchValue : undefined,
    300,
  );
  const nonSearchParamsKey = useMemo(() => JSON.stringify(
    Object.keys(params)
      .filter(key => key !== searchField)
      .sort()
      .map(key => [key, (params as Record<string, unknown>)[key]]),
  ), [params, searchField]);
  const requestParams = useMemo<P>(() => {
    const nextParams = Object.fromEntries(JSON.parse(nonSearchParamsKey)) as Record<string, unknown>;
    delete nextParams[searchField as string];
    nextParams[searchField as string] = debouncedSearchValue || undefined;
    return nextParams as P;
  }, [debouncedSearchValue, nonSearchParamsKey, searchField]);
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => [resourceKey, requestParams] as const, [resourceKey, requestParams]);
  const query = useQuery({
    queryKey,
    queryFn: () => fetchPage(requestParams),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
  const reload = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [resourceKey] });
  }, [queryClient, resourceKey]);

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ? rateLimitErrorMessage(query.error, errorFallback, t) : null,
    reload,
  };
};
